/**
 * Entity editor for the service-mapping store.
 *
 * Services and providers get purpose-built forms because they carry the real
 * content — relations, contact channels, hours, coordinates. Every other kind
 * is a name plus an optional definition, so they share one simple form.
 *
 * Directus groups the service's ~40 fields into collapsible panels; types.ts
 * flattens those (they hold no data) and this component reconstructs them as
 * the sections below.
 */

import { useState, useEffect, useMemo, useCallback, type ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Switch } from "@/components/ui/switch"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { ChevronDown, ChevronRight, Plus, Trash2, X, Check, History } from "lucide-react"
import { useRecordDraft } from "../shared/useRecordDraft"
import { RecordStatusBar, type RecordStatus } from "../shared/components/RecordStatusBar"
import { BUCKET } from "./types"
import * as db from "./store"
import { ServiceMap, CoordinateReadout } from "./ServiceMap"
import {
  type ServiceStore, type EntityKind, displayName, pickTranslated,
  type HourDay,
  CONTACT_CHANNELS, HOUR_DAYS, SERVICE_STATUSES, PROVIDER_STATUSES,
  serviceStatusFor,
} from "./types"
import type { EntityVersion } from "./history"
import { HistoryPanel, type HistoryVersionView } from "../shared/components/HistoryPanel"
import { Section, Field, ToggleField } from "../shared/components/EditorFields"
import { ImageField } from "../shared/components/ImageField"
import { describeLanguage, translationStatus, type LanguageInfo } from "../shared/languages"
import { isSiteLocale, unsupportedLocaleMessage } from "../shared/siteLanguages"
import { TranslationStatusBar } from "../shared/components/languages/TranslationStatusBar"
import { ServiceFields } from "./editor/ServiceFields"
import { ProviderFields } from "./editor/ProviderFields"
import { CategoryFields } from "./editor/CategoryFields"

interface ServiceEditorProps {
  store: ServiceStore
  // The working language, shared with the tree and the other sections
  locale: string
  languages: LanguageInfo[]
  kind: EntityKind
  id: string
  onUpdate: (kind: EntityKind, id: string, fields: Record<string, any>) => void
  onSetTranslation: (kind: EntityKind, id: string, locale: string, patch: Record<string, any>) => void
  // Lists the working language in the Service Map, when it isn't yet
  onAddLocale: (code: string) => void
  onClose: () => void
  onSelect: (kind: EntityKind, id: string) => void
  // Saves the editor's draft of the record as one change; false when refused
  onCommit: (kind: EntityKind, id: string, entity: any) => boolean
  // Where the record stands and what to do next; shown in the strip under the header
  status?: RecordStatus
  // Header chip: whether the record is indexed for meaning search
  indexBadge?: ReactNode
  /** Recorded versions for one entity, newest first. Omit to hide the History button entirely. */
  versionsOf?: (entityId: string) => EntityVersion[]
  /** Restores an entity to a previously-recorded version. */
  restoreEntityVersion?: (entityId: string, entryId: string, kind: EntityKind) =>
    { ok: true; description: string } | { ok: false; error: string }
}

const SERVICE_DRAFT_OPS = {
  bucket: BUCKET as Record<EntityKind, string>,
  getEntity: db.getEntity, updateEntity: db.updateEntity, setTranslation: db.setTranslation,
}

export function ServiceEditor(props: ServiceEditorProps) {
  const {
    locale, languages, kind, id, onAddLocale, onClose, onSelect,
    versionsOf, restoreEntityVersion,
  } = props
  // Fields edit a draft; Save writes it as one version
  const draft = useRecordDraft({
    section: "services", store: props.store, kind, id, ops: SERVICE_DRAFT_OPS,
    onCommit: props.onCommit, onUpdate: props.onUpdate, onSetTranslation: props.onSetTranslation,
  })
  const { store, onUpdate, onSetTranslation } = draft
  const entity = db.getEntity(store, kind, id)
  // As last saved, for the header
  const savedEntity = db.getEntity(props.store, kind, id)
  // Translated fields edit the working language
  const editLocale = locale
  const [historyOpen, setHistoryOpen] = useState(false)
  const [restoringId, setRestoringId] = useState<string | null>(null)

  if (!entity) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
        This record no longer exists.
      </div>
    )
  }

  const t = entity.translations?.[editLocale] ?? {}
  const fallback = pickTranslated(entity, editLocale, store.defaultLocale)
  const title = displayName(entity, locale, store.defaultLocale)
  const sourceLocale = store.defaultLocale
  const language = languages.find(l => l.code === locale) ?? describeLanguage(locale)
  const source = languages.find(l => l.code === sourceLocale) ?? describeLanguage(sourceLocale)
  const status = translationStatus(entity.translations, locale, sourceLocale)
  const sourceT = entity.translations?.[sourceLocale]

  // A code the sites can't show, that the Service Map doesn't list either, can't be written in
  const blocked = !store.languages[editLocale] && !isSiteLocale(editLocale)
  const setT = (patch: Record<string, any>) => { if (!blocked) onSetTranslation(kind, id, editLocale, patch) }
  const set = (patch: Record<string, any>) => onUpdate(kind, id, patch)

  const rawVersions = versionsOf ? versionsOf(id) : []
  const currentEntryId = rawVersions.find(v => !v.isPriorState)?.entryId
  const versionViews: HistoryVersionView[] = rawVersions.map(v => ({
    id: v.entryId,
    at: v.at,
    action: v.action,
    author: v.author,
    actor: v.actor,
    summary: v.summary,
    isCurrent: v.entryId === currentEntryId,
    restoreUnavailable: !v.entity,
    snapshot: v.entity,
  }))

  const handleRestoreVersion = (entryId: string) => {
    if (!restoreEntityVersion) return
    setRestoringId(entryId)
    try {
      // The restored version replaces any unsaved edits
      draft.discard()
      const result = restoreEntityVersion(id, entryId, kind)
      if (result.ok) setHistoryOpen(false)
    } finally {
      setRestoringId(null)
    }
  }

  // The source text as a starting point; it stays unreviewed
  const startFromSource = () => {
    if (!sourceT) return
    setT({
      name: sourceT.name ?? "", description: sourceT.description, address: sourceT.address,
      definition: sourceT.definition, reviewed: false,
    })
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start gap-2 border-b p-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">{kind}</Badge>
            {props.indexBadge}
          </div>
          <h2 className="mt-1 truncate text-base font-semibold">{title || "Untitled"}</h2>
          {savedEntity?.updatedAt && (
            <p className="text-[11px] text-muted-foreground">
              Updated {new Date(savedEntity.updatedAt).toLocaleString()}
              {savedEntity.createdAt ? ` · created ${new Date(savedEntity.createdAt).toLocaleDateString()}` : ""}
            </p>
          )}
          {kind === "service" && db.servicePath(store, id, locale) !== title && (
            <p className="truncate text-xs text-muted-foreground">{db.servicePath(store, id, locale)}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {versionsOf && (
            <Button
              variant="ghost" size="icon" className="h-8 w-8 relative"
              onClick={() => setHistoryOpen(true)}
              title={versionViews.length ? `${versionViews.length} recorded version(s)` : "No recorded changes yet"}
            >
              <History className="h-4 w-4" />
              {versionViews.length > 0 && (
                <Badge variant="secondary" className="absolute -right-1 -top-1 h-4 min-w-4 px-1 text-[9px]">
                  {versionViews.length}
                </Badge>
              )}
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <RecordStatusBar
        status={props.status}
        unsaved={draft.dirty ? { onSave: draft.save, onDiscard: draft.discard, changedElsewhere: draft.changedElsewhere } : null}
      />

      {versionsOf && restoreEntityVersion && (
        <HistoryPanel
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          title={`${kind}: ${title || "(untitled)"}`}
          versions={versionViews}
          onRestore={handleRestoreVersion}
          restoringId={restoringId}
        />
      )}

      <TranslationStatusBar
        language={language}
        source={source}
        status={status}
        onToggleReviewed={() => setT({ reviewed: status !== "reviewed" })}
        onStartFromSource={sourceT ? startFromSource : undefined}
        readOnly={blocked}
        unsupported={isSiteLocale(locale) ? undefined : unsupportedLocaleMessage(locale)}
        notListed={store.languages[locale] ? undefined : {
          message: `${language.name} isn't a Service Map language yet, so readers can't choose it.`,
          action: "Add to Service Map",
          onAdd: () => onAddLocale(locale),
        }}
      />

      <ScrollArea className="flex-1 min-h-0 [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
        <div className="space-y-4 p-3">
          {/* Translated fields — every kind has these */}
          <Section title={`Content · ${language.name}`} defaultOpen>
            <Field label="Name">
              <Input
                dir={language.direction}
                value={t.name ?? ""}
                placeholder={editLocale === store.defaultLocale ? "" : fallback?.name ?? ""}
                onChange={e => setT({ name: e.target.value })}
                className="h-8 text-sm"
              />
            </Field>
            {(kind === "service" || kind === "provider") ? (
              <Field label="Description" hint="HTML — keep markup when translating">
                <Textarea
                  dir={language.direction}
                  value={t.description ?? ""}
                  placeholder={editLocale === store.defaultLocale ? "" : fallback?.description ?? ""}
                  onChange={e => setT({ description: e.target.value })}
                  rows={6}
                  className="text-sm font-mono"
                />
              </Field>
            ) : (
              <Field label="Definition" hint="Markdown">
                <Textarea
                  dir={language.direction}
                  value={t.definition ?? ""}
                  placeholder={editLocale === store.defaultLocale ? "" : fallback?.definition ?? ""}
                  onChange={e => setT({ definition: e.target.value })}
                  rows={4}
                  className="text-sm"
                />
              </Field>
            )}
            {kind === "service" && (
              <Field label="Address">
                <Input
                  dir={language.direction}
                  value={t.address ?? ""}
                  placeholder={editLocale === store.defaultLocale ? "" : fallback?.address ?? ""}
                  onChange={e => setT({ address: e.target.value })}
                  className="h-8 text-sm"
                />
              </Field>
            )}
            {entity.translations?.[editLocale]?.updatedAt && (
              <p className="text-[11px] text-muted-foreground">
                {language.name} last edited {new Date(entity.translations[editLocale].updatedAt).toLocaleString()}
              </p>
            )}
          </Section>

          {kind === "service" && (
            <ServiceFields
              store={store} locale={locale} editLocale={editLocale}
              service={entity} set={set} setT={setT} onSelect={onSelect}
            />
          )}
          {kind === "provider" && (
            <ProviderFields store={store} locale={locale} provider={entity} set={set} onSelect={onSelect} />
          )}
          {kind === "serviceCategory" && (
            <CategoryFields category={entity} set={set} />
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

// ── Service form ─────────────────────────────────────────────────────────────

